// BGM 트랙 선택(순수)과 재생기(HTMLAudioElement를 WebAudio로 연결, 1초 교차 페이드).
// 음원은 public/assets/audio/bgm/{village,dungeon,boss,clear}.mp3 + 레이드 곡 public/assets/audio/raid/raid.mp3(tools/audio/raid-bgm.ts 절차 작곡).
// 미디어 로딩과 실제 재생 실패를 따로 추적한다.
import { BossState, Scene, combatScene } from '../shared/types';
import { resolveAssetUrl } from '../pwa/assetMap';

export const BGM_TRACKS = ['village', 'dungeon', 'boss'] as const;
export type BgmTrack = (typeof BGM_TRACKS)[number];
/** 재생기가 다루는 모든 곡(BGM_TRACKS = audio/bgm/ 폴더 곡 + 레이드 곡) */
/** 엔딩 곡: 기본 clear + 지역별 clear-r1..clear-r6(파일이 없으면 기본 곡으로 대신한다, BgmPlayer) */
export const CLEAR_REGIONS = 6;
/** 지역별 던전·보스 곡(지역5·6). 번호는 clear-r6 뒤(11..14)라 기존 곡 번호는 그대로. 파일이 없으면 지역 공통 곡(dungeon·boss) */
export const REGION_TRACKS = ['dungeon-r5', 'boss-r5', 'dungeon-r6', 'boss-r6'] as const;
/** 주간 이벤트 곡(3개월 C8 FlowMusic, public/assets/audio/live/<이름>.mp3). 이벤트 주의 마을에서 마을 곡 대신 튼다. 번호는 지역 곡 뒤(15..19) */
export const LIVE_TRACKS = ['live_gold', 'live_mine', 'live_boss', 'live_tower', 'live_festival'] as const;
export const ALL_TRACKS = [...BGM_TRACKS, 'raid', 'clear', ...Array.from({ length: CLEAR_REGIONS }, (_, i) => `clear-r${i + 1}`), ...REGION_TRACKS, ...LIVE_TRACKS] as const;
/** public/assets/audio/bgm/ 에 실제로 있는 지역별 던전·보스 곡 이름. 없는 곡은 요청하지 않고 처음부터 지역 공통 곡을 쓴다. 파일을 넣으면 여기에도 추가한다(bgm.test.ts가 대조) */
export const REGION_TRACK_FILES: ReadonlySet<string> = new Set<string>(['dungeon-r5', 'boss-r5', 'dungeon-r6', 'boss-r6']);
/** public/assets/audio/bgm/ 에 실제로 있는 지역별 엔딩 곡(지역 번호 1..6). 없는 지역은 요청하지 않고 처음부터 기본 엔딩 곡(clear)을 쓴다. 파일을 넣으면 여기에도 추가한다(bgm.test.ts가 대조) */
export const CLEAR_REGION_FILES: ReadonlySet<number> = new Set<number>([5, 6]);
export const BgmId = { None: -1, Village: 0, Dungeon: 1, Boss: 2, Raid: 3, Clear: 4 } as const;
/** 지역(1..6) 엔딩 곡 번호. 지역을 모르면(0 등) 기본 엔딩 곡 */
/** 첫 이벤트 곡 번호(지역 엔딩 곡·지역 던전/보스 곡 뒤) */
export const LIVE_BASE = BgmId.Clear + CLEAR_REGIONS + 1 + REGION_TRACKS.length;
/** 이벤트 주차 bgm 키(liveops.ts `live_gold_rush` 등) → 곡 번호. public/assets/audio/live/manifest.json bgmKeyToTrack과 같은 묶음(bgm.test.ts가 대조) */
export const LIVE_KEY_TO_TRACK: Readonly<Record<string, (typeof LIVE_TRACKS)[number]>> = {
  live_gold_rush: 'live_gold', live_golden_weekend: 'live_gold', live_treasure_hunt: 'live_gold',
  live_crystal_vein: 'live_mine', live_miners_week: 'live_mine', live_collectors_week: 'live_mine',
  live_boss_rush: 'live_boss', live_champion_trial: 'live_boss',
  live_tower_season: 'live_tower', live_tower_assault: 'live_tower', live_season_finale: 'live_tower',
  live_bounty_festival: 'live_festival', live_forge_fair: 'live_festival',
};
/** 이벤트 bgm 키 → 곡 번호(모르는 키·빈 키는 -1 = 이벤트 곡 없음) */
export function liveTrackOf(key: string): number {
  const name = LIVE_KEY_TO_TRACK[key];
  return name ? LIVE_BASE + LIVE_TRACKS.indexOf(name) : BgmId.None;
}
/** 지금 열린 주간 이벤트의 bgm 키. 마을 일일 게시판(dailyPanel)이 장부를 볼 때 채우고 오디오가 읽는다(menuBadges와 같은 공유 칸) */
export const liveMusic = { key: '' };
export function clearTrackOf(region: number): number {
  return Number.isInteger(region) && region >= 1 && region <= CLEAR_REGIONS ? BgmId.Clear + region : BgmId.Clear;
}
/** 결과 화면의 지역 번호(1..): 스테이지 id(r2-s10·h2-s10) 먼저, 없으면 지도 id(r2), 모르면 0(기본 엔딩 곡) */
export function regionOfResult(stageId: string | undefined, mapId: string | undefined): number {
  const m = /^[rhx](\d)-s\d\d$/.exec(stageId ?? '') ?? /^r(\d)$/.exec(mapId ?? '');
  return m ? Number(m[1]) : 0;
}
/** 지역별 던전/보스 곡 번호(base = BgmId.Dungeon|Boss). 그 지역 곡 파일이 없으면 base 그대로 */
export function regionTrackOf(base: number, region: number, files: ReadonlySet<string> = REGION_TRACK_FILES): number {
  if (base !== BgmId.Dungeon && base !== BgmId.Boss) return base;
  const name = BGM_TRACKS[base] + '-r' + region;
  const i = (ALL_TRACKS as readonly string[]).indexOf(name);
  return i >= 0 && files.has(name) ? i : base;
}
/** 지역 곡이 다시 실패했을 때 대신할 곡: 던전·보스 지역 곡 → 공통 곡, 지역 엔딩 곡 → 기본 엔딩 곡 */
function fallbackOf(i: number): number {
  if (i < 0) return i;
  const name = ALL_TRACKS[i] as string;
  if (name.startsWith('dungeon-r')) return BgmId.Dungeon;
  if (name.startsWith('boss-r')) return BgmId.Boss;
  if (i >= LIVE_BASE) return BgmId.Village; // 이벤트 곡 파일이 없으면 마을 곡
  return i > BgmId.Clear ? BgmId.Clear : i;
}
function isRegional(i: number): boolean {
  return fallbackOf(i) !== i;
}
/** 곡 번호 → 자산 경로(assets/ 기준). 레이드 곡만 레이드 폴더에 있고, 엔딩 곡(clear·clear-r1..6)은 bgm 폴더에 있다(tools/audio/clear-bgm.ts 절차 작곡 + 지휘 쪽 FlowMusic 지역별 곡) */
export function trackPath(i: number, base = 'assets/audio/bgm/'): string {
  if (i >= LIVE_BASE) return 'assets/audio/live/' + ALL_TRACKS[i] + '.mp3';
  return i === BgmId.Raid ? 'assets/audio/raid/raid.mp3' : i >= BgmId.Clear ? base + ALL_TRACKS[i] + '.mp3' : base + BGM_TRACKS[i] + '.mp3';
}

/** 장면·보스 상태 → 트랙 번호. 보스 등장부터 쓰러지는 연출까지 보스 곡(레이드 판이면 레이드 곡), 패배 화면은 무음.
 *  live: 이벤트 곡 번호(liveTrackOf, 없으면 -1) — 이벤트 주의 마을에서만 마을 곡 대신 튼다(타이틀·결과 화면은 그대로) */
export function chooseTrack(scene: number, bossState: number, raid = false, region = 0, live: number = BgmId.None): number {
  if (scene === Scene.Village && live >= LIVE_BASE) return live;
  // 결과 화면: 일반 던전은 엔딩 곡(승리 징글 뒤 반복, 지역별 곡이 있으면 그 곡), 레이드는 마을 곡
  if (scene === Scene.Result) return raid ? BgmId.Village : clearTrackOf(region);
  if (scene === Scene.Title || scene === Scene.Village) return BgmId.Village;
  if (combatScene(scene)) {
    const bossOn = bossState !== BossState.Inactive && bossState !== BossState.Dead;
    return bossOn ? (raid ? BgmId.Raid : regionTrackOf(BgmId.Boss, region)) : regionTrackOf(BgmId.Dungeon, region);
  }
  return BgmId.None;
}

export const BGM_FADE = 1.0;
/** 없는 파일을 다시 확인하기까지의 간격(초) */
const RECHECK = 30;

type Status = 'unknown' | 'checking' | 'ready' | 'missing';

export class BgmPlayer {
  private readonly els: (HTMLAudioElement | null)[] = ALL_TRACKS.map(() => null);
  private readonly sources: (MediaElementAudioSourceNode | null)[] = ALL_TRACKS.map(() => null);
  private readonly pending = ALL_TRACKS.map(() => false);
  private readonly retryAt = new Float64Array(ALL_TRACKS.length);
  private readonly errors = ALL_TRACKS.map(() => '');
  private readonly gains: (GainNode | null)[] = ALL_TRACKS.map(() => null);
  private readonly status: Status[] = ALL_TRACKS.map((): Status => 'unknown');
  private readonly checkedAt = new Float64Array(ALL_TRACKS.length);
  private current: number = BgmId.None;
  /** 장면이 원하는 곡(지역 엔딩 곡 파일이 없으면 current는 기본 엔딩 곡) */
  private wanted: number = BgmId.None;
  private hidden = false;

  constructor(private readonly ctx: AudioContext, private readonly out: AudioNode, private readonly base = 'assets/audio/bgm/') {}

  get track(): number {
    return this.current;
  }

  statusOf(i: number): string {
    return this.status[i];
  }

  playbackOf(i: number): { paused: boolean; pending: boolean; error: string } {
    return { paused: this.els[i]?.paused ?? true, pending: this.pending[i], error: this.errors[i] };
  }

  /** 트랙 음량(교차 페이드 진행, QA용) */
  gainOf(i: number): number {
    const g = this.gains[i];
    return g ? g.gain.value : 0;
  }

  /** 트랙 재생 위치(초, QA용). 없으면 -1 */
  timeOf(i: number): number {
    const el = this.els[i];
    return el ? el.currentTime : -1;
  }

  /** 트랙 바꾸기(같으면 아무 일 없음). 교차 페이드 1초. 지역 엔딩 곡 파일이 없으면 기본 엔딩 곡으로 대신한다 */
  select(want: number): void {
    this.wanted = want;
    const name = want >= 0 ? (ALL_TRACKS[want] as string) : '';
    // 지역 엔딩·던전·보스 곡은 목록에 없는 파일이면 처음부터 대신 곡, 이벤트 곡은 오류(missing)일 때만 마을 곡으로
    const unlisted = isRegional(want) && want < LIVE_BASE && (want > BgmId.Clear + CLEAR_REGIONS ? !REGION_TRACK_FILES.has(name) : !CLEAR_REGION_FILES.has(want - BgmId.Clear));
    const track = unlisted || (this.status[want] === 'missing' && isRegional(want)) ? fallbackOf(want) : want;
    if (track === this.current) return;
    const prev = this.current;
    this.current = track;
    if (prev >= 0) this.fade(prev, 0);
    if (track >= 0) this.ensure(track);
  }

  /** 첫 사용자 입력·탭 복귀 뒤 현재 트랙 재생을 다시 시도한다(자동재생 정책). */
  kick(): void {
    const i = this.current;
    if (i < 0 || this.hidden) return;
    if (this.status[i] === 'missing' && performance.now() / 1000 - this.checkedAt[i] > RECHECK) this.status[i] = 'unknown';
    if (this.status[i] === 'unknown') this.ensure(i);
    else if (this.els[i]) this.start(i);
  }

  /** 재생 거부·일시 중단은 1초 간격, 파일 오류는 30초 간격으로 복구한다. */
  poll(nowSec: number): void {
    const i = this.current;
    if (i < 0 || this.hidden || this.ctx.state !== 'running') return;
    if (this.status[i] === 'missing') {
      if (nowSec - this.checkedAt[i] >= RECHECK) this.ensure(i);
    } else if (this.els[i]?.paused && !this.pending[i] && nowSec >= this.retryAt[i]) this.start(i);
  }

  setHidden(hidden: boolean): void {
    this.hidden = hidden;
    if (hidden) {
      for (const el of this.els) el?.pause();
    } else this.kick();
  }

  private ensure(i: number): void {
    const now = performance.now() / 1000;
    if (this.els[i] && this.status[i] !== 'missing') { this.start(i); return; }
    if (this.status[i] === 'missing' && now - this.checkedAt[i] < RECHECK) return;
    // 별도 HEAD 완료를 기다리면 최초 입력의 재생 허용 시점을 놓친다.
    // 오디오 요소를 바로 준비하고 실패는 미디어 error와 play 거부로 구분한다.
    const old = this.els[i];
    if (old) { old.pause(); old.removeAttribute('src'); old.load(); }
    this.sources[i]?.disconnect();
    this.gains[i]?.disconnect();
    const el = new Audio();
    el.crossOrigin = 'anonymous';
    el.loop = true;
    el.preload = 'auto';
    const g = this.ctx.createGain();
    g.gain.value = 0;
    const source = this.ctx.createMediaElementSource(el);
    source.connect(g); g.connect(this.out);
    this.els[i] = el; this.gains[i] = g; this.sources[i] = source;
    this.pending[i] = false;
    this.status[i] = 'checking';
    this.errors[i] = '';
    el.addEventListener('canplay', () => {
      if (this.els[i] !== el) return;
      this.status[i] = 'ready';
      if (this.current === i) this.start(i);
    });
    el.addEventListener('error', () => {
      if (this.els[i] !== el) return;
      this.status[i] = 'missing';
      this.checkedAt[i] = performance.now() / 1000;
      this.errors[i] = 'media:' + (el.error?.code ?? 'unknown');
      this.pending[i] = false;
      // 지역 엔딩 곡 파일이 없으면 기본 엔딩 곡으로
      // (이벤트 곡이 없으면 마을 곡으로)
      if (isRegional(i) && this.wanted === i) this.select(i);
    });
    el.src = resolveAssetUrl(trackPath(i, this.base));
    el.load();
    if (this.current === i) this.start(i);
  }

  private start(i: number): void {
    const el = this.els[i];
    if (!el || this.hidden || this.current !== i || this.ctx.state !== 'running') return;
    if (!el.paused) { this.fade(i, 1); return; }
    if (this.pending[i]) return;
    this.pending[i] = true;
    this.retryAt[i] = performance.now() / 1000 + 1;
    if (this.gains[i]!.gain.value < 0.01) el.currentTime = 0;
    void el.play().then(() => {
      if (this.els[i] !== el) return;
      this.pending[i] = false;
      this.errors[i] = '';
      // 늦게 성공한 이전 장면의 곡은 현재 곡을 덮지 않는다.
      if (this.current !== i || this.hidden) { el.pause(); return; }
      this.fade(i, 1);
    }).catch((error: unknown) => {
      if (this.els[i] !== el) return;
      this.pending[i] = false;
      this.errors[i] = error instanceof Error ? error.name : String(error);
    });
  }

  private fade(i: number, target: number): void {
    const g = this.gains[i];
    if (!g) return;
    const t = this.ctx.currentTime;
    g.gain.cancelScheduledValues(t);
    g.gain.setValueAtTime(g.gain.value, t);
    g.gain.linearRampToValueAtTime(target, t + BGM_FADE);
    if (target === 0) {
      const el = this.els[i];
      setTimeout(() => { if (this.current !== i && el) el.pause(); }, BGM_FADE * 1000 + 100);
    }
  }
}
